import { Landmark, Wallet, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export function PaymentMethods({ paymentInfo, copyToClipboard }) {
  return (
    <div className="mt-6 pt-6 border-t border-[#E7E5E4]" data-testid="payment-methods-section">
      <div className="flex items-center gap-2 mb-4">
        <Landmark className="w-4 h-4 text-[#264639]" />
        <p className="text-sm font-medium text-[#1C1917]">Métodos de pago</p>
      </div>

      {paymentInfo.holder_name && (
        <p className="text-sm text-[#57534E] mb-3">
          <span className="font-medium text-[#1C1917]">Titular:</span> {paymentInfo.holder_name}
        </p>
      )}

      <div className="space-y-3">
        <MercadoPagoSection data={paymentInfo.mercado_pago} copyToClipboard={copyToClipboard} />
        <ARSSection data={paymentInfo.ars} copyToClipboard={copyToClipboard} />
        <PYGSection data={paymentInfo.pyg} copyToClipboard={copyToClipboard} />
        <BRLSection data={paymentInfo.brl} copyToClipboard={copyToClipboard} />
      </div>

      {paymentInfo.notes && (
        <p className="text-xs text-[#78716C] mt-3 italic">{paymentInfo.notes}</p>
      )}
    </div>
  );
}

function CopyButton({ text, copyToClipboard }) {
  return (
    <button onClick={() => copyToClipboard(text)} className="p-1 hover:bg-white/50 rounded">
      <Copy className="w-3 h-3 text-[#78716C]" />
    </button>
  );
}

function MercadoPagoSection({ data, copyToClipboard }) {
  if (!data?.alias && !data?.cvu) return null;

  return (
    <div className="bg-[#E5F6FF] rounded-lg p-3" data-testid="payment-mp">
      <div className="flex items-center gap-2 mb-2">
        <Wallet className="w-4 h-4 text-[#009EE3]" />
        <span className="text-xs font-semibold text-[#009EE3] uppercase">Mercado Pago</span>
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          {data.alias && (
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-[#57534E]">Alias: <span className="font-mono font-medium text-[#1C1917]">{data.alias}</span></span>
              <CopyButton text={data.alias} copyToClipboard={copyToClipboard} />
            </div>
          )}
          {data.cvu && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#57534E]">CVU: <span className="font-mono text-xs text-[#1C1917]">{data.cvu}</span></span>
              <CopyButton text={data.cvu} copyToClipboard={copyToClipboard} />
            </div>
          )}
        </div>
        {(data.alias || data.cvu) && (
          <div className="flex-shrink-0 bg-white rounded-lg p-1.5" data-testid="mp-qr-code">
            <QRCodeSVG value={data.cvu || data.alias} size={64} level="M" fgColor="#009EE3" bgColor="#FFFFFF" />
          </div>
        )}
      </div>
    </div>
  );
}

function ARSSection({ data, copyToClipboard }) {
  if (!data?.cbu && !data?.cvu && !data?.alias) return null;

  return (
    <div className="bg-[#F0F4FF] rounded-lg p-3" data-testid="payment-ars">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm">🇦🇷</span>
        <span className="text-xs font-semibold text-[#3B5998] uppercase">Pesos Argentinos</span>
      </div>
      {data.bank && <p className="text-xs text-[#57534E] mb-1">Banco: {data.bank}</p>}
      {data.cbu && (
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-[#57534E]">CBU: <span className="font-mono text-xs text-[#1C1917]">{data.cbu}</span></span>
          <CopyButton text={data.cbu} copyToClipboard={copyToClipboard} />
        </div>
      )}
      {data.alias && (
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-[#57534E]">Alias: <span className="font-mono font-medium text-[#1C1917]">{data.alias}</span></span>
          <CopyButton text={data.alias} copyToClipboard={copyToClipboard} />
        </div>
      )}
      {data.cvu && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#57534E]">CVU: <span className="font-mono text-xs text-[#1C1917]">{data.cvu}</span></span>
          <CopyButton text={data.cvu} copyToClipboard={copyToClipboard} />
        </div>
      )}
      {data.cuit && <p className="text-xs text-[#78716C] mt-1">CUIT: {data.cuit}</p>}
    </div>
  );
}

function PYGSection({ data, copyToClipboard }) {
  if (!data?.bank && !data?.account_number) return null;

  return (
    <div className="bg-[#FFF5F0] rounded-lg p-3" data-testid="payment-pyg">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm">🇵🇾</span>
        <span className="text-xs font-semibold text-[#C36F4D] uppercase">Guaraníes</span>
      </div>
      {data.bank && <p className="text-xs text-[#57534E] mb-1">Banco: {data.bank}</p>}
      {data.account_number && (
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-[#57534E]">Cuenta: <span className="font-mono text-[#1C1917]">{data.account_number}</span></span>
          <CopyButton text={data.account_number} copyToClipboard={copyToClipboard} />
        </div>
      )}
      {data.account_type && <p className="text-xs text-[#78716C]">{data.account_type}</p>}
    </div>
  );
}

function BRLSection({ data, copyToClipboard }) {
  if (!data?.pix_key && !data?.bank) return null;

  return (
    <div className="bg-[#F0FFF4] rounded-lg p-3" data-testid="payment-brl">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm">🇧🇷</span>
        <span className="text-xs font-semibold text-[#2D8C41] uppercase">Reales</span>
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          {data.bank && <p className="text-xs text-[#57534E] mb-1">Banco: {data.bank}</p>}
          {data.pix_key && (
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-[#57534E]">PIX: <span className="font-mono text-[#1C1917]">{data.pix_key}</span></span>
              <CopyButton text={data.pix_key} copyToClipboard={copyToClipboard} />
            </div>
          )}
        </div>
        {data.pix_key && (
          <div className="flex-shrink-0 bg-white rounded-lg p-1.5" data-testid="pix-qr-code">
            <QRCodeSVG value={data.pix_key} size={64} level="M" fgColor="#2D8C41" bgColor="#FFFFFF" />
          </div>
        )}
      </div>
    </div>
  );
}
